import {ChangeDetectionStrategy, Component, DestroyRef, input, Input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {BeslutningModell, BeslutningsforslagSpesifikasjon, BeslutningsforslagSpesifikasjoner, GruppeId, HtmlTekst, MedlemId, StorIkkeTomStreng} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {leggTilFormArray, medDeaktiverteRaderMidlertidigReaktivert} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {siste} from "felles-ng2/støttefunksjoner/lister";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {konsumerTilDestroy} from "felles-ng2/infrastruktur/rxjs/observerbarHjelpere";
import {NgClass} from "@angular/common";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {TranslateModule} from "@ngx-translate/core";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {nil} from "felles-ng2/typer/nullish";

const debug = initDebugger('BeslutningSkjemaKontroller');

export type BeslutningerSkjema = ReturnType<typeof BeslutningSkjemaKontroller.opprettSkjema>;
export type BeslutningerSkjemaVerdier = BeslutningerSkjema['value'];
export type BeslutningSkjema = ReturnType<typeof BeslutningSkjemaKontroller.opprettSkjemarad>
export type BeslutningSkjemaVerdier = BeslutningSkjema['value'];
export type Deltaker = { medlem: { id: MedlemId }; skalSignere?: boolean };


@Component({
    selector: 'beslutninger-skjema',
    templateUrl: './beslutninger-skjema.html',
    styleUrls: ['./beslutninger-skjema.scss'],
    imports: [
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        NgClass,
        MatButtonModule,
        MatIconModule,
        TranslateModule,
        StandardValideringsfeilPipe,
        RiktekstRedigeringKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BeslutningSkjemaKontroller {

    /** Oppretter nytt tomt skjema */
    static opprettSkjema() {
        return new FormArray<BeslutningSkjema>([])
    }

    /** Oppretter og populerer skjema med beslutninger */
    static opprettSkjemaMedRader(beslutninger: BeslutningModell[]): BeslutningerSkjema {
        return beslutninger
          .reduce(
            (skjema, beslutning) => leggTilFormArray(skjema, BeslutningSkjemaKontroller.opprettSkjemarad({
                ...beslutning,
                tittel: erIkkeNil(beslutning.erForslag) ? beslutning.tittelForslag! : beslutning.tittel!,
                beskrivelse: erIkkeNil(beslutning.erForslag) ? beslutning.beskrivelseForslag : beslutning.beskrivelse,
                ny: false
            })),
            BeslutningSkjemaKontroller.opprettSkjema())
    }

    static opprettSkjemarad(verdier = {id: nyUuid(), tittel: '', beskrivelse: '', ny: true}) {
        return new FormGroup({
            id: new FormControl<MedlemId>(verdier.id, {nonNullable: true}),
            tittel: new FormControl<StorIkkeTomStreng>(verdier.tittel, {validators: FeltValidatorer.storStreng}),
            beskrivelse: new FormControl<HtmlTekst>(verdier.beskrivelse, {validators: FeltValidatorer.htmlTekst}),
            slettet: new FormControl<boolean>(false, {nonNullable: true}),
            ny: new FormControl<boolean>(verdier.ny, {nonNullable: true})
        });
    }

    static somBeslutningsforslagSpesifikasjoner(skjema: BeslutningerSkjema): BeslutningsforslagSpesifikasjoner {
        //pakker inn i medDeaktiverteRaderMidlertidigReaktivert for å få med eksisterende beslutninger som har blitt slettet
        return medDeaktiverteRaderMidlertidigReaktivert(skjema, skjema => {
            return skjema.value
              //utelat nye rader uten tittel og nye rader markert som slettet
              .filter(beslutning => !(beslutning.ny && (erTomEllerNil(beslutning.tittel) || beslutning.slettet)))
              .reduce((beslutninger, beslutning) => {
                  const spesifikasjon = somBeslutningsforslagSpesifikasjon(beslutning);
                  if (beslutning.ny) beslutninger.nyeBeslutninger.push(spesifikasjon);
                  else beslutninger.eksisterendeBeslutninger.push(spesifikasjon);
                  return beslutninger;
              }, {eksisterendeBeslutninger: [], nyeBeslutninger: []} as BeslutningsforslagSpesifikasjoner);
        });
    }

    gruppeId = input<GruppeId | nil>(null);

    private _skjema!: BeslutningerSkjema;

    constructor(private destroyRef: DestroyRef) {
    }

    @Input({required: true}) set skjema(skjema: BeslutningerSkjema) {
        this._skjema = skjema;
        this.initSkjema();
    }

    get skjema() {
        return this._skjema;
    }

    erTomRad(rad: BeslutningSkjema): boolean {
        return erTomRad(rad.value);
    }

    slett(radnummer: number) {
        const rad = this.skjema.at(radnummer);
        rad.disable();
        //dersom rad ikke er tom viser vi "angre slett" knapp
        if (erIkkeTomRad(rad.value))
            rad.controls.slettet.setValue(true);
        else {
            this.skjema.removeAt(radnummer);
            //sørger for at siste rad ikke er påkrevet (tom linje til slutt for reg av ny beslutning)
            this.skjema.at(this.skjema.controls.length - 1).controls.tittel.removeValidators(Validators.required);
        }
    }

    angreSlett(radnummer: number) {
        const rad = this.skjema.at(radnummer);
        rad.enable();
        rad.controls.slettet.setValue(false);
    }

    // private hjelpemetoder

    private initSkjema() {
        debug('initSkjema', this.skjema)
        //sørger for at det finnes en tom linje til slutt for reg av ny beslutning
        this.skjema.push(BeslutningSkjemaKontroller.opprettSkjemarad());

        //reager ved endringer i skjema-verdier
        konsumerTilDestroy(this.skjema.valueChanges, this.destroyRef,
          skjemaverdier => this.vedEndringer(skjemaverdier));
    }

    private vedEndringer(skjemaverdier: BeslutningerSkjemaVerdier) {
        debug('endringer', skjemaverdier)
        //legg til ny rad dersom siste rad er ikke-tom
        if (erIkkeTomRad(siste(skjemaverdier)!)) {
            this.skjema.controls.filter(rad => rad.value.ny).forEach(rad => rad.controls.tittel.addValidators(Validators.required));
            this.skjema.push(BeslutningSkjemaKontroller.opprettSkjemarad());
        }

        //dersom rad er tom, representerer en ny beslutning og ikke er siste fjerner vi den
        skjemaverdier.forEach((verdi, indeks) => {
            if (erTomRad(verdi) && verdi.ny && indeks < skjemaverdier.length - 1)
                this.skjema.removeAt(indeks);
        });

        //valider at ikke både tittel og beskrivelse er tomme for eksisterende beslutninger
        //valider at tittel er ikke-tomm for nye beslutninger (unntatt for siste rad)
        this.skjema.controls
          .filter((rad, indeks) => indeks < this.skjema.controls.length - 1)
          .forEach(rad => rad.value.ny ? this.validerNyBeslutning(rad) : this.validerEksisterendeBeslutning(rad))
    }

    private validerEksisterendeBeslutning(rad: BeslutningSkjema) {
        if (erTomEllerNil(rad.controls.tittel.value) && erTomEllerNil(rad.controls.beskrivelse.value))
            rad.controls.tittel.setErrors({tittelOgBeskrivelseTom: true});
    }

    private validerNyBeslutning(rad: BeslutningSkjema) {
        if (erTomEllerNil(rad.controls.tittel.value))
            rad.controls.tittel.setErrors({required: true});
    }
}


function erIkkeTomRad(rad: BeslutningSkjemaVerdier) {
    return !erTomRad(rad);
}

function erTomRad(rad: BeslutningSkjemaVerdier) {
    return erTomEllerNil(rad.tittel) && erTomEllerNil(rad.beskrivelse);
}

function somBeslutningsforslagSpesifikasjon(beslutning: BeslutningSkjemaVerdier): BeslutningsforslagSpesifikasjon {
    return {id: beslutning.id!, tittel: beslutning.tittel!, beskrivelse: beslutning.beskrivelse!, slettet: beslutning.slettet!};
}
